import React, { useState, useCallback, useRef } from 'react';
import Sidebar from '../components/Sidebar';

const API = (path) =>
  fetch(path, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function OwnerClients() {
  const [search, setSearch]     = useState('');
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState(null);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults([]); setSearched(false); return; }
    setLoading(true);
    setError('');
    const res = await API(`/api/owner/clients?search=${encodeURIComponent(q)}`);
    const d = await res.json();
    setLoading(false);
    setSearched(true);
    if (!res.ok) { setError(d.error || 'Search failed'); return; }
    setResults(d.clients || []);
  }, []);

  const handleInput = (e) => {
    const q = e.target.value;
    setSearch(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 350);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    clearTimeout(debounceRef.current);
    runSearch(search);
  };

  const stageColor = (stage) => {
    const map = {
      pipeline:  ['#dbeafe','#1e40af'],
      completed: ['#d1fae5','#065f46'],
      cancelled: ['#fee2e2','#991b1b'],
      on_hold:   ['#fef3c7','#92400e'],
    };
    return map[stage] || ['#f3f4f6','#374151'];
  };

  const fmt = (v) => v ? `£${parseFloat(v).toLocaleString('en-GB', { minimumFractionDigits: 0 })}` : '—';
  const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-GB') : '—';

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Client Lookup</h1>
        <p style={{ color: '#6b7280', marginBottom: '28px' }}>
          Search any client across all broker companies — use when a broker calls about a client issue
        </p>

        {/* Search bar */}
        <form onSubmit={handleSubmit} style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', gap: '12px', maxWidth: '640px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', pointerEvents: 'none' }}>🔍</span>
              <input
                value={search}
                onChange={handleInput}
                placeholder="Search by client name, email, phone or deal reference…"
                autoFocus
                style={{
                  width: '100%', padding: '12px 14px 12px 44px',
                  border: '2px solid #e5e7eb', borderRadius: '10px',
                  fontSize: '15px', boxSizing: 'border-box',
                  outline: 'none', transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = '#3b82f6'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <button type="submit" style={{
              background: '#1e40af', color: '#fff', border: 'none',
              borderRadius: '10px', padding: '12px 24px', fontSize: '15px',
              cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
            }}>
              Search
            </button>
          </div>
          <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
            Searches across all deals on the platform. Min 2 characters.
          </p>
        </form>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Searching…</div>
        )}

        {/* No results */}
        {!loading && searched && results.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔍</div>
            <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>No clients found</div>
            <div style={{ fontSize: '14px' }}>Try a different name, email, phone number or deal reference</div>
          </div>
        )}

        {/* Results */}
        {!loading && results.length > 0 && (
          <>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              {results.length} result{results.length !== 1 ? 's' : ''} found
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {results.map(c => {
                const isOpen = expanded === c.deal_id;
                const [stageBg, stageText] = stageColor(c.stage);
                const docTotal    = parseInt(c.total_docs || 0);
                const docApproved = parseInt(c.approved_docs || 0);
                const docPct      = docTotal > 0 ? Math.round((docApproved / docTotal) * 100) : null;
                const hasPortal   = !!c.portal_invite_id;
                const portalActive = c.portal_accepted_at;

                return (
                  <div key={c.deal_id} style={{
                    background: '#fff', borderRadius: '12px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    border: '1px solid #e5e7eb', overflow: 'hidden',
                  }}>
                    {/* Summary row */}
                    <div
                      onClick={() => setExpanded(isOpen ? null : c.deal_id)}
                      style={{ padding: '18px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: '44px', height: '44px', borderRadius: '50%',
                        background: '#1e40af', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: '18px', flexShrink: 0,
                      }}>
                        {(c.client_name || '?').charAt(0).toUpperCase()}
                      </div>

                      {/* Name + contact */}
                      <div style={{ flex: 1, minWidth: '160px' }}>
                        <div style={{ fontWeight: 700, fontSize: '15px' }}>{c.client_name}</div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {[c.client_email, c.client_phone].filter(Boolean).join(' · ')}
                        </div>
                      </div>

                      {/* Company */}
                      <div style={{ minWidth: '130px' }}>
                        <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600, marginBottom: '2px' }}>Company</div>
                        <div style={{ fontSize: '13px', fontWeight: 500 }}>{c.company_name}</div>
                      </div>

                      {/* Broker */}
                      <div style={{ minWidth: '130px' }}>
                        <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600, marginBottom: '2px' }}>Broker</div>
                        <div style={{ fontSize: '13px' }}>{c.broker_name || '—'}</div>
                      </div>

                      {/* Stage */}
                      <div>
                        <span style={{
                          background: stageBg, color: stageText,
                          padding: '4px 12px', borderRadius: '999px',
                          fontSize: '12px', fontWeight: 600, textTransform: 'capitalize',
                        }}>
                          {(c.stage || '').replace('_', ' ')}
                        </span>
                      </div>

                      {/* Ref */}
                      {c.reference && (
                        <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280', background: '#f3f4f6', padding: '4px 10px', borderRadius: '6px' }}>
                          {c.reference}
                        </div>
                      )}

                      <span style={{ color: '#9ca3af', fontSize: '18px', marginLeft: 'auto' }}>{isOpen ? '▲' : '▼'}</span>
                    </div>

                    {/* Expanded detail */}
                    {isOpen && (
                      <div style={{ borderTop: '1px solid #f3f4f6', background: '#fafafa', padding: '20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>

                          {/* Deal info */}
                          <Panel title="Deal">
                            <Row label="Reference"    value={c.reference || '—'} mono />
                            <Row label="Stage"        value={(c.stage || '').replace('_',' ')} capitalize />
                            <Row label="Status code"  value={c.current_status_code || '—'} />
                            <Row label="Fee"          value={fmt(c.actual_fee)} />
                            <Row label="Fee status"   value={c.fee_status || '—'} capitalize />
                            <Row label="Deal opened"  value={fmtDate(c.deal_created_at)} />
                            <Row label="Completed"    value={fmtDate(c.completion_date)} />
                          </Panel>

                          {/* People */}
                          <Panel title="People">
                            <Row label="Broker company"  value={c.company_name} />
                            <Row label="Assigned broker" value={c.broker_name || 'Unassigned'} />
                            {c.broker_email && <Row label="Broker email" value={c.broker_email} />}
                            <Row label="Introducer"      value={c.introducer_name || 'None'} />
                          </Panel>

                          {/* Contact */}
                          <Panel title="Client Contact">
                            <Row label="Email"   value={c.client_email || '—'} />
                            <Row label="Phone"   value={c.client_phone || '—'} />
                            <Row label="Address" value={c.client_address || '—'} />
                          </Panel>

                          {/* Enquiry */}
                          {c.enquiry_id && (
                            <Panel title="Original Enquiry">
                              <Row label="Product"  value={c.product_type || '—'} />
                              <Row label="Source"   value={c.source || '—'} capitalize />
                              <Row label="Urgency"  value={c.urgency || '—'} capitalize />
                              <Row label="Status"   value={(c.enquiry_status || '').replace('_',' ')} capitalize />
                              <Row label="Received" value={fmtDate(c.enquiry_created_at)} />
                            </Panel>
                          )}

                          {/* Document portal */}
                          <Panel title="Client Document Portal">
                            {!hasPortal ? (
                              <div style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic' }}>No portal invite sent</div>
                            ) : (
                              <>
                                <Row label="Invite email" value={c.portal_invite_email || '—'} />
                                <Row label="Accepted"     value={portalActive ? fmtDate(c.portal_accepted_at) : 'Not yet'} />
                                <Row label="Expires"      value={fmtDate(c.portal_expires_at)} />
                                {docTotal > 0 ? (
                                  <>
                                    <Row label="Documents" value={`${docApproved} / ${docTotal} approved`} />
                                    <div style={{ marginTop: '8px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ flex: 1, background: '#e5e7eb', borderRadius: '999px', height: '8px', overflow: 'hidden' }}>
                                          <div style={{
                                            width: `${docPct}%`,
                                            background: docPct === 100 ? '#10b981' : '#3b82f6',
                                            height: '100%', borderRadius: '999px',
                                          }} />
                                        </div>
                                        <span style={{ fontSize: '12px', fontWeight: 700, color: docPct === 100 ? '#10b981' : '#3b82f6' }}>{docPct}%</span>
                                      </div>
                                      {parseInt(c.rejected_docs) > 0 && (
                                        <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>{c.rejected_docs} document{c.rejected_docs > 1 ? 's' : ''} rejected</div>
                                      )}
                                      {parseInt(c.pending_docs) > 0 && (
                                        <div style={{ fontSize: '12px', color: '#f59e0b', marginTop: '2px' }}>{c.pending_docs} pending broker review</div>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <Row label="Documents" value="No documents yet" />
                                )}
                              </>
                            )}
                          </Panel>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Empty state before search */}
        {!loading && !searched && (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: '#d1d5db' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>🔍</div>
            <div style={{ fontSize: '16px', color: '#9ca3af' }}>Start typing to search for a client</div>
          </div>
        )}
      </main>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: '10px', padding: '14px 16px', border: '1px solid #e5e7eb' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, mono, capitalize }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f3f4f6', gap: '8px' }}>
      <span style={{ fontSize: '12px', color: '#9ca3af', flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: '13px', color: '#374151', fontWeight: 500,
        textAlign: 'right', wordBreak: 'break-word',
        fontFamily: mono ? 'monospace' : undefined,
        textTransform: capitalize ? 'capitalize' : undefined,
      }}>
        {value}
      </span>
    </div>
  );
}
